<template>
  <div>
    <!-- Breadcrumb -->
    <b-card class="mb-3">
      <b-breadcrumb>
        <b-breadcrumb-item to="/">
          <feather-icon
            icon="HomeIcon"
            size="16"
            class="mr-25"
          />
          <span class="align-middle">Accueil</span>
        </b-breadcrumb-item>
        <b-breadcrumb-item :to="{ name: 'pickupeur-list' }">
          Ramasseurs
        </b-breadcrumb-item>
        <b-breadcrumb-item :to="{ name: 'pickupeur-show', params: { id: $route.params.id }}">
          Détail ramasseur
        </b-breadcrumb-item>
        <b-breadcrumb-item active>
          Changer le mot de passe
        </b-breadcrumb-item>
      </b-breadcrumb>
    </b-card>

    <b-overlay
      :show="isLoading"
      rounded="sm"
      spinner-variant="primary"
    >
      <b-alert
        v-if="error"
        variant="danger"
        show
      >
        {{ error }}
      </b-alert>

      <b-card title="Changer le mot de passe">
        <validation-observer
          ref="formValidator"
          v-slot="{ handleSubmit }"
        >
          <b-form @submit.prevent="handleSubmit(onSubmit)">
            <b-card-text class="mb-3">
              <div class="alert alert-info">
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="mr-1"
                />
                <span>Vous êtes en train de modifier le mot de passe pour le ramasseur : <strong>{{ pickupeurName }}</strong></span>
              </div>
            </b-card-text>

            <b-row>
              <!-- New Password -->
              <b-col md="6">
                <b-form-group
                  label="Nouveau mot de passe *"
                  label-for="password"
                  description="Le mot de passe doit comporter au moins 8 caractères."
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Mot de passe"
                    rules="required|min:8"
                    vid="password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        id="password"
                        v-model="passwordForm.password"
                        :type="showPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : passwordStrength > 25"
                        placeholder="Entrez le nouveau mot de passe"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          @click="showPassword = !showPassword"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>

                    <!-- Password Strength Indicator -->
                    <div class="mt-1">
                      <div class="d-flex justify-content-between mb-1">
                        <small>Force du mot de passe :</small>
                        <small class="text-right">{{ passwordStrengthText }}</small>
                      </div>
                      <b-progress
                        :value="passwordStrength"
                        :variant="passwordStrengthClass"
                        max="100"
                        height="8px"
                      />
                    </div>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Confirm Password -->
              <b-col md="6">
                <b-form-group
                  label="Confirmer le mot de passe *"
                  label-for="confirmPassword"
                  description="Ressaisissez le mot de passe pour confirmation."
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Confirmation du mot de passe"
                    rules="required|confirmed:password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        id="confirmPassword"
                        v-model="passwordForm.confirm_password"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Confirmez le mot de passe"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          @click="showConfirmPassword = !showConfirmPassword"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <div class="d-flex justify-content-between mt-4">
              <b-button
                variant="outline-secondary"
                :to="{ name: 'pickupeur-show', params: { id: $route.params.id }}"
              >
                <feather-icon
                  icon="XIcon"
                  class="mr-1"
                />
                <span>Annuler</span>
              </b-button>

              <b-button
                variant="primary"
                type="submit"
                :disabled="isSubmitting || !canSubmit"
              >
                <feather-icon
                  icon="SaveIcon"
                  class="mr-1"
                />
                <span>Enregistrer</span>
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, confirmed, min } from 'vee-validate/dist/rules'
import storeAuth from '@/store/store'
import {
  BCard,
  BBreadcrumb,
  BBreadcrumbItem,
  BButton,
  BAlert,
  BOverlay,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BCardText,
  BRow,
  BCol,
  BProgress,
} from 'bootstrap-vue'

extend('required', required)
extend('confirmed', confirmed)
extend('min', min)

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BCard,
    BBreadcrumb,
    BBreadcrumbItem,
    BButton,
    BAlert,
    BOverlay,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BCardText,
    BRow,
    BCol,
    BProgress,
  },

  data() {
    return {
      passwordForm: {
        password: '',
        confirm_password: '',
      },
      pickupeur: {},
      isLoading: false,
      isSubmitting: false,
      error: null,
      showPassword: false,
      showConfirmPassword: false,
      user: {},
    }
  },

  computed: {
    pickupeurName() {
      return this.pickupeur.username || ''
    },

    passwordStrength() {
      let strength = 0
      const { password } = this.passwordForm

      if (!password) return 0

      if (password.length >= 8) strength += 25
      if (/[A-Z]/.test(password)) strength += 25
      if (/[0-9]/.test(password)) strength += 25
      if (/[^A-Za-z0-9]/.test(password)) strength += 25

      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    canSubmit() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordForm.password === this.passwordForm.confirm_password
        && this.passwordForm.password.length >= 8
      )
    },
  },

  created() {
    this.user = storeAuth.state.user
    if (this.user.role !== 'admin') {
      this.error = "Vous n'avez pas l'autorisation de changer les mots de passe."
    } else {
      this.getPickupeurDetails()
    }
  },

  methods: {
    async getPickupeurDetails() {
      this.isLoading = true
      try {
        const { id } = this.$route.params
        const { data } = await axios.get(`/api/auth/users/pickupeurs/${id}/`)
        this.pickupeur = data
      } catch (error) {
        console.error('Error fetching pickupeur details:', error)
        this.error = 'Erreur lors du chargement des informations du ramasseur'
      } finally {
        this.isLoading = false
      }
    },

    async onSubmit() {
      if (!this.canSubmit) return

      this.isSubmitting = true
      try {
        const { id } = this.$route.params
        await axios.post(`/api/auth/users/pickupeurs/change-password/${id}/`, this.passwordForm)

        this.showToast('success', 'Mot de passe modifié avec succès')
        this.$router.push({ name: 'pickupeur-show', params: { id } })
      } catch (error) {
        console.error('Error changing password:', error)
        this.showToast('danger', error.response?.data?.error || 'Erreur lors du changement de mot de passe')
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
          text,
          variant,
        },
      }, {
        position: 'top-right',
      })
    },
  },
}
</script>
